/**
 * Icon picker component for chat agents.
 * Displays a grid of lucide icons in a popover.
 */

import * as React from 'react';
import {
  Bot,
  Brain,
  BriefcaseBusiness,
  ChartBar,
  Code,
  Cog,
  Database,
  FileText,
  FlaskConical,
  Globe,
  GraduationCap,
  Heart,
  Image,
  Lightbulb,
  Mail,
  MessageCircle,
  Mic,
  Music,
  Pencil,
  Rocket,
  Search,
  Shield,
  ShoppingCart,
  Sparkles,
  Star,
  Target,
  Terminal,
  TrendingUp,
  Users,
  Wand2,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export const AGENT_ICONS: Record<string, LucideIcon> = {
  bot: Bot,
  brain: Brain,
  briefcase: BriefcaseBusiness,
  chart: ChartBar,
  code: Code,
  cog: Cog,
  database: Database,
  file: FileText,
  flask: FlaskConical,
  globe: Globe,
  graduation: GraduationCap,
  heart: Heart,
  image: Image,
  lightbulb: Lightbulb,
  mail: Mail,
  message: MessageCircle,
  mic: Mic,
  music: Music,
  pencil: Pencil,
  rocket: Rocket,
  search: Search,
  shield: Shield,
  cart: ShoppingCart,
  sparkles: Sparkles,
  star: Star,
  target: Target,
  terminal: Terminal,
  trending: TrendingUp,
  users: Users,
  wand: Wand2,
  zap: Zap,
};

export function getAgentIcon(iconName: string): LucideIcon {
  return AGENT_ICONS[iconName] || Bot;
}

interface IconPickerProps {
  value?: string;
  onChange?: (icon: string) => void;
  disabled?: boolean;
}

export function IconPicker({
  value = 'bot',
  onChange,
  disabled = false,
}: IconPickerProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const SelectedIcon = getAgentIcon(value);

  const handleSelect = (iconName: string) => {
    onChange?.(iconName);
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          disabled={disabled}
          className='h-10 w-10 p-0'
          type='button'
        >
          <SelectedIcon className='h-5 w-5' />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-72 p-3' align='start'>
        <h4 className='text-sm font-medium mb-2'>Choose an icon</h4>
        <div className='grid grid-cols-8 gap-1'>
          {Object.entries(AGENT_ICONS).map(([name, Icon]) => (
            <button
              key={name}
              type='button'
              onClick={() => handleSelect(name)}
              className={cn(
                'flex items-center justify-center h-8 w-8 rounded-md transition-colors',
                'hover:bg-accent hover:text-accent-foreground',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                value === name
                  ? 'bg-primary/10 text-primary ring-1 ring-primary/30'
                  : 'text-muted-foreground',
              )}
              title={name}
            >
              <Icon className='h-4 w-4' />
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
